import React, { useState, useRef, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import DownArrow from "../../../../assets/icons/DownArrow";
import withdrawIcon from "../../../../assets/withdraw.svg";
import depositeIcon from "../../../../assets/Deposite.svg";
import FinancialSingleDatePicker, {
  formatDateInputValue,
} from "./FinancialSingleDatePicker";

const getInitialTransactionDate = (transaction) => {
  if (transaction?.tnx_date) return transaction.tnx_date;
  if (transaction?.date instanceof Date) {
    return formatDateInputValue(transaction.date);
  }
  return formatDateInputValue(new Date());
};

function EditJournalModal({ onClose, onSubmit, transaction }) {
  const todayDateValue = useMemo(() => formatDateInputValue(new Date()), []);
  const [transactionType, setTransactionType] = useState(transaction?.transactionType || "");
  const [showDropdown, setShowDropdown] = useState(false);
  const [amount, setAmount] = useState(transaction?.amount?.toString() || "");
  const [note, setNote] = useState(transaction?.note === "—" ? "" : transaction?.note || "");
  const [transactionDate, setTransactionDate] = useState(() =>
    getInitialTransactionDate(transaction)
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const sanitizeAmount = (val) => {
    let v = String(val || "").replace(/[^0-9.]/g, "");
    const parts = v.split(".");
    if (parts.length > 2) {
      v = parts[0] + "." + parts.slice(1).join("");
    }
    return v;
  };

  const handleAmountChange = (e) => setAmount(sanitizeAmount(e.target.value));

  const handleSubmit = async () => {
    const amountValue = parseFloat(amount);
    if (!amount || Number.isNaN(amountValue) || amountValue <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (!transactionDate) {
      toast.error("Please select a transaction date");
      return;
    }

    if (transactionDate > todayDateValue) {
      toast.error("Transaction date cannot be in the future");
      return;
    }

    if (!onSubmit) {
      onClose();
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        transactionType,
        amount: amountValue,
        note: note.trim(),
        tnx_date: transactionDate,
      });
    } catch {
      // Error toast handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedIcon =
    transactionType === "Withdraw"
      ? withdrawIcon
      : transactionType === "Deposit"
        ? depositeIcon
        : null;

  return (
    <div className="add-journal-backdrop" onClick={isSubmitting ? undefined : onClose}>
      <div className="add-journal-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <h3 className="add-journal-header">Edit Journal</h3>
        <p className="add-journal-subtitle">Update transaction details</p>
        <hr className="add-journal-divider" />

        <label className="add-journal-field-label">Transaction Type</label>
        <div className="add-journal-select" ref={dropdownRef}>
          <button type="button" className="add-journal-select-inner" onClick={() => setShowDropdown((s) => !s)}>
            {selectedIcon ? (
              <img src={selectedIcon} alt={transactionType} className="add-journal-select-icon-img" />
            ) : (
              <span className="add-journal-select-icon-placeholder" />
            )}
            <span className={`add-journal-select-text ${!transactionType ? 'is-placeholder' : ''}`}>{transactionType || 'Select transaction type'}</span>
            <span className="add-journal-caret"><DownArrow /></span>
          </button>

          {showDropdown && (
            <div className="add-journal-status-dropdown">
              <div className="add-journal-status-item" onClick={() => { setTransactionType("Withdraw"); setShowDropdown(false); }}>
                <img src={withdrawIcon} alt="Withdraw" className="add-journal-select-icon-img" />
                <span>Withdraw</span>
              </div>
              <div className="add-journal-status-item" onClick={() => { setTransactionType("Deposit"); setShowDropdown(false); }}>
                <img src={depositeIcon} alt="Deposit" className="add-journal-select-icon-img" />
                <span>Deposit</span>
              </div>
            </div>
          )}
        </div>

        <label className="add-journal-field-label">Amount *</label>
        <div className="add-journal-input">€ <input inputMode="decimal" pattern="[0-9]*" value={amount} onChange={handleAmountChange} placeholder="Enter Amount" /></div>

        <label className="add-journal-field-label">Date *</label>
        <FinancialSingleDatePicker
          value={transactionDate}
          maxDate={todayDateValue}
          onChange={setTransactionDate}
        />

        <label className="add-journal-field-label">Note</label>
        <div className="add-journal-textarea"><textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Enter your note" /></div>

        <div className="add-journal-actions">
          <button type="button" className="add-journal-btn-secondary" onClick={onClose} disabled={isSubmitting}>Cancel</button>
          <button type="button" className="add-journal-btn-primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Updating..." : "Update Journal"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditJournalModal;
